import { useEffect, useState } from 'react';
import { Configuration, OrdersApi } from '@/api';
import { Order, OrderAssociation, Orders } from '@/models/Order';
import { EmptyResult } from '@/models/Pagination';
import { TimeWindow } from '@/models/TimeWindow';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { setPaginationResultIfNotLoading } from '@/utils/pagination';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const initialState = EmptyResult<Order>();

export interface OrderFilters {
  storeId?: string;
  supplierId?: string;
  status?: string;
  period?: TimeWindow;
}

interface FindOrdersQueryResult {
  orders: Orders;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  itemsPerPage: number;
  invalidateQuery: () => Promise<void>;
}

const useFindOrdersQuery = (
  page: number,
  searchQuery?: string,
  filters?: Partial<OrderFilters>,
): FindOrdersQueryResult => {
  const queryClient = useQueryClient();
  const [orders, setOrders] = useState<Orders>(initialState);

  const ordersApi = new OrdersApi(new Configuration(useAuth));

  const itemsPerPage = 10;

  const { isLoading, data, isFetching, error } = useQuery({
    queryKey: ['orders', page, searchQuery, filters],
    queryFn: () => {
      return ordersApi.findOrders(
        searchQuery,
        {
          storeId: filters?.storeId ?? undefined,
          supplierId: filters?.supplierId ?? undefined,
          status: filters?.status ?? undefined,
          period: filters?.period ?? undefined,
        },
        page * itemsPerPage - itemsPerPage,
        itemsPerPage,
        [OrderAssociation.SUPPLIER, OrderAssociation.STORE],
      );
    },
  });

  useEffect(() => {
    setPaginationResultIfNotLoading(data, setOrders, isLoading);
  }, [data, isLoading, isFetching]);

  const invalidateQuery = async () => {
    return queryClient.invalidateQueries({
      predicate: (q) => q.queryKey[0] === 'orders',
    });
  };

  return {
    orders,
    isLoading,
    isFetching,
    error,
    itemsPerPage,
    invalidateQuery,
  };
};

export default useFindOrdersQuery;
